import type {
  Sdk as MagentoApiSdk,
  SdkFunctionWrapper,
} from "@magento-api-v2/__generated__/magento-api-gql-request-sdk";
import { getSdk as getMagentoApiSdk } from "@magento-api-v2/__generated__/magento-api-gql-request-sdk";
import debug from "debug";
import { GraphQLClient as GraphQLRequestClient } from "graphql-request";

import { type Config, getConfig, getSelectedRegionId, isServerSide } from "@jti/fe-common";
import { getUser } from "@jti/rest-api";

import { MagentoGraphQLRequestClientError } from "../MagentoGraphQLRequestClientError";

import { validateMagentoErrorCodes } from "./utils/validateMagentoErrorCodes";

export type RequestConfig = typeof GraphQLRequestClient.prototype.requestConfig;

type MagentoApiClientConfig = {
  magentoServiceUrl: Config["MAGENTO_SERVICE_URL"];
  trialStoreView: Config["trials-store-view"];
  defaultStoreView: Config["default-store-view"];
};

type MagentoApiRequestOptions = {
  method?: RequestConfig["method"];
  isTrial?: boolean;
};

type MagentoApiRequestFacade = MagentoApiSdk & ((options?: MagentoApiRequestOptions) => MagentoApiSdk);

export class MagentoApiClientV2 {
  private static instance: MagentoApiClientV2;
  private static isConstructing = false;
  private config: MagentoApiClientConfig;
  private sdk: MagentoApiSdk;
  private requestFacade: MagentoApiRequestFacade;
  static log = debug("desjti:magento-api-client-v2");

  private constructor(config?: Config) {
    if (!MagentoApiClientV2.isConstructing) {
      throw new Error(
        "MagentoApiClientV2 is a singleton. Use MagentoApiClientV2.init() instead of new MagentoApiClientV2()."
      );
    }

    const {
      MAGENTO_SERVICE_URL,
      "trials-store-view": trialStoreView,
      "default-store-view": defaultStoreView,
    } = config || getConfig();

    if (!MAGENTO_SERVICE_URL) {
      throw new Error("MAGENTO_SERVICE_URL is not defined in the configuration.");
    }

    this.config = {
      magentoServiceUrl: MAGENTO_SERVICE_URL,
      trialStoreView,
      defaultStoreView,
    };

    const baseRequestOptions = this.getBaseRequestOptions();

    const client = new GraphQLRequestClient(this.config.magentoServiceUrl, baseRequestOptions);

    this.sdk = getMagentoApiSdk(client, this.sdkErrorHandlingWrapper);
    this.requestFacade = this.createRequestFacade();
  }

  static init(config?: Config): MagentoApiClientV2 {
    if (MagentoApiClientV2.instance) {
      return MagentoApiClientV2.instance;
    }

    if (isServerSide() && !config) {
      MagentoApiClientV2.log(
        "[MagentoApiClientV2] Initializing on the server side without config. This throws an error because config is required on the server side."
      );
      throw new Error("Config must be provided when initializing MagentoApiClientV2 on the server side.");
    }

    MagentoApiClientV2.isConstructing = true;

    try {
      MagentoApiClientV2.instance = new MagentoApiClientV2(config);
    } finally {
      MagentoApiClientV2.isConstructing = false;
    }

    return MagentoApiClientV2.instance;
  }

  private sdkErrorHandlingWrapper: SdkFunctionWrapper = async (action) => {
    try {
      return await action();
    } catch (error) {
      const isWrappedGraphQLError = MagentoGraphQLRequestClientError.isMagentoGraphQLRequestClientError(error);
      const graphQLError = isWrappedGraphQLError
        ? error
        : MagentoGraphQLRequestClientError.isGraphQLRequestError(error)
          ? new MagentoGraphQLRequestClientError(error)
          : undefined;

      if (graphQLError) {
        validateMagentoErrorCodes(graphQLError.errorCodes, graphQLError.errorsDetails);
      }

      MagentoApiClientV2.log(
        "[MagentoApiClientV2] An error occurred during a GraphQL request:",
        graphQLError?.response ?? error
      );

      // Preserve the existing V2 error contract; the wrapper is used only for session-expiry classification.
      throw error;
    }
  };

  private createRequestFacade(): MagentoApiRequestFacade {
    const request = ((options?: MagentoApiRequestOptions) =>
      this.getSdkForRequestOptions(options)) as MagentoApiRequestFacade;

    Object.assign(request, this.sdk);

    return request;
  }

  private getSdkForRequestOptions(options?: MagentoApiRequestOptions): MagentoApiSdk {
    const shouldOverrideMethod = Boolean(options?.method && options.method !== "POST");
    const shouldOverrideStore = Boolean(options?.isTrial);

    if (!shouldOverrideMethod && !shouldOverrideStore) {
      return this.sdk;
    }

    const requestConfig = this.getBaseRequestOptions(options);
    const client = new GraphQLRequestClient(this.config.magentoServiceUrl, requestConfig);

    return getMagentoApiSdk(client, this.sdkErrorHandlingWrapper);
  }

  private getBaseRequestOptions(options?: MagentoApiRequestOptions): RequestConfig {
    const { magento_token: magentoToken } = getUser() || {};
    const baseRequestOptions: RequestConfig = {};

    const headers: Record<string, string> = {};

    if (magentoToken) {
      headers.Authorization = `Bearer ${magentoToken}`;
    }

    headers.Store = options?.isTrial ? this.config.trialStoreView : this.config.defaultStoreView;

    const selectedRegionId = getSelectedRegionId();
    if (selectedRegionId) {
      headers["X-Selected-Region-Id"] = selectedRegionId;
    }

    if (options?.method && options.method !== "POST") {
      baseRequestOptions.method = options.method;
    }

    baseRequestOptions.jsonSerializer = JSON;
    baseRequestOptions.headers = headers;

    return baseRequestOptions;
  }

  get request(): MagentoApiRequestFacade {
    return this.requestFacade;
  }
}
